/**
 * Traditional MIPS quality category score (top 6) — TypeScript mirror of
 * api/services/mipsCompositeScoring.js calculateQualityScore, which remains the
 * stored-score authority. Kept in lockstep by __tests__/quality-category.parity.test.ts.
 *
 * Rules: measures without finite points are skipped; points desc, ties by ascending
 * measure ID (CMS PY2026 guide §2.8); one slot per measure ID (best collection type);
 * the best Outcome OR High-Priority measure is guaranteed a slot (Traditional submission
 * strategy, backlog B19 — a lower-scoring outcome is assumed left out of the submission;
 * MVP is outcome-first); with neither, only 5 measures count (the 6th slot scores 0);
 * small practices get +6; the category is capped at 60 points; score = points / 60 × weight.
 */
import type { PracticeSize } from '@/lib/mips-scoring';

export interface CategoryMeasure {
  measureId: string;
  submissionMethod?: string;
  points: number | null | undefined;
  isOutcome?: boolean;
  isHighPriority?: boolean;
}

export interface SelectedCategoryMeasure extends CategoryMeasure {
  points: number;
}

export interface QualityCategoryResult {
  /** Weighted category score, rounded to 2 decimals (what the backend stores as quality_score). */
  score: number;
  /** Raw points of the selected measures, before the small-practice bonus. */
  pointsEarned: number;
  smallPracticeBonus: number;
  /** min(pointsEarned + bonus, 60). */
  adjustedPoints: number;
  selected: SelectedCategoryMeasure[];
  skipped: string[];
  /** Lower-scoring duplicate collection-type instances that lost their slot. */
  dedupDropped: SelectedCategoryMeasure[];
}

/** A measure as POSTed to /mips/scores/calculate (see category-payload.ts). */
export interface ScorePayloadMeasure {
  measure_id?: string;
  measureId?: string;
  submission_method?: string;
  calculated_points?: number | null;
  is_outcome?: boolean;
  is_high_priority?: boolean;
  measure_type?: string;
}

/** Read a score payload measure exactly as the backend does. */
export function fromScorePayload(m: ScorePayloadMeasure): CategoryMeasure {
  return {
    measureId: (m.measure_id || m.measureId) as string,
    submissionMethod: m.submission_method,
    points: m.calculated_points,
    isOutcome: Boolean(m.is_outcome || m.measure_type === 'outcome'),
    isHighPriority: Boolean(m.is_high_priority || m.measure_type === 'highPriority'),
  };
}

const MAX_MEASURES = 6;
const MAX_POINTS = 60;
const SMALL_PRACTICE_BONUS = 6;

export function computeQualityCategory(
  measures: CategoryMeasure[],
  qualityWeight: number,
  practiceSize: PracticeSize = 'large',
): QualityCategoryResult {
  if (!measures || measures.length === 0) {
    return { score: 0, pointsEarned: 0, smallPracticeBonus: 0, adjustedPoints: 0, selected: [], skipped: [], dedupDropped: [] };
  }

  const skipped: string[] = [];
  const scored: SelectedCategoryMeasure[] = [];
  for (const m of measures) {
    const points = Number(m.points);
    if (m.points === undefined || m.points === null || !Number.isFinite(points)) {
      skipped.push(m.measureId);
      continue;
    }
    scored.push({ ...m, points });
  }

  scored.sort((a, b) =>
    b.points - a.points ||
    String(a.measureId).localeCompare(String(b.measureId), undefined, { numeric: true, sensitivity: 'base' }));

  const bestById = new Map<string, SelectedCategoryMeasure>();
  const dedupDropped: SelectedCategoryMeasure[] = [];
  for (const m of scored) {
    if (!bestById.has(m.measureId)) bestById.set(m.measureId, m);
    else dedupDropped.push(m);
  }
  const deduped = [...bestById.values()];

  const selected: SelectedCategoryMeasure[] = [];
  const priority = deduped.find(m => m.isOutcome || m.isHighPriority);
  if (priority) selected.push(priority);
  const slots = priority ? MAX_MEASURES : MAX_MEASURES - 1;
  for (const m of deduped) {
    if (selected.length >= slots) break;
    if (!selected.includes(m)) selected.push(m);
  }

  const pointsEarned = selected.reduce((sum, m) => sum + m.points, 0);
  const smallPracticeBonus = practiceSize === 'small' ? SMALL_PRACTICE_BONUS : 0;
  const adjustedPoints = Math.min(pointsEarned + smallPracticeBonus, MAX_POINTS);
  const score = Math.round((adjustedPoints / MAX_POINTS) * qualityWeight * 100) / 100;

  return { score, pointsEarned, smallPracticeBonus, adjustedPoints, selected, skipped, dedupDropped };
}
